import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import 'bootstrap/dist/css/bootstrap.min.css';

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters'),

  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters'),

userName: yup
    .string()
    .required('User name is required')
    .min(3, 'User name must be at least 3 characters'),

  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email format'),

  contactNo: yup
    .string()
    .required('Contact number is required')
    .matches(/^[0-9]{10}$/, 'Contact number must be 10 digits')
    .transform((value) => value.replace(/\D/g, '')),

  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),

  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password'), null], 'Passwords must match'),

  address: yup
    .string()
    .required('Address is required')
    .min(10, 'Address must be at least 10 characters')
    .max(200, 'Address must be less than 200 characters'),
});

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  // Watch the password field for real-time updates
  const passwordValue = watch("password", "");

//   const onSubmit = async (data) => {
//     setIsSubmitting(true);
//     try {
//       // Simulate API call
//       console.log('Registration data:', data);
//       await new Promise(resolve => setTimeout(resolve, 1500));
//
//       alert('Registration successful! Welcome to our platform.');
//       reset();
//     } catch (error) {
//       alert('Registration failed. Please try again.');
//     } finally {
//       setIsSubmitting(false);
//     }
//   };
const onSubmit = async (data) => {
  setIsSubmitting(true);

  const payload = {
    first_name: data.firstName,
    last_name: data.lastName,
    username: data.userName,
    email: data.email,
    password: data.password,
    contact_no: data.contactNo,
    address: data.address,
  };

  try {
    const response = await fetch("http://127.0.0.1:8000/api/users/register/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const err = await response.json();
      throw err;
    }

    alert("Registration successful!");
    reset();
  } catch (error) {
    console.error(error);
    alert("Registration failed");
  } finally {
    setIsSubmitting(false);
  }
};

  const handleContactChange = (e) => {
    // Remove all non-digits
    let value = e.target.value.replace(/\D/g, '');

    // Limit to 10 digits (Nepali phone numbers are 10 digits)
    if (value.length > 10) {
      value = value.slice(0, 10);
    }

    e.target.value = value;

    // Trigger form update
    e.target.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: 'Very Weak', color: 'danger' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;

    const levels = [
      { label: 'Very Weak', color: 'danger', strength: 1 },
      { label: 'Weak', color: 'warning', strength: 2 },
      { label: 'Fair', color: 'info', strength: 3 },
      { label: 'Good', color: 'primary', strength: 4 },
      { label: 'Strong', color: 'success', strength: 5 },
    ];

    return levels[Math.min(strength - 1, 4)] || { label: 'Very Weak', color: 'danger', strength: 0 };
  };

  // Calculate password strength based on current value
  const passwordStrength = getPasswordStrength(passwordValue);
  const progressWidth = passwordStrength.strength * 20;

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8 col-md-10">
          <div className="card shadow-lg border-0 rounded-lg mt-5">
            <div className="card-header bg-gradient-primary text-dark">
              <h3 className="text-center font-weight-light my-2 text-dark">SIGN UP</h3>
{/*               <p className="text-center mb-0">Join our community today!</p> */}
            </div>

            <div className="card-body p-4 p-md-5">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Name Row */}
                <div className="row mb-4">
                  <div className="col-md-6 mb-3">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        id="firstName"
                        placeholder="John"
                        {...register('firstName')}
                      />
                      <label htmlFor="firstName">First Name *</label>
                      {errors.firstName && (
                        <div className="invalid-feedback d-block">
                          {errors.firstName.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        id="lastName"
                        placeholder="Doe"
                        {...register('lastName')}
                      />
                      <label htmlFor="lastName">Last Name *</label>
                      {errors.lastName && (
                        <div className="invalid-feedback d-block">
                          {errors.lastName.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

            {/* Username */}
                <div className="mb-4">
                  <div className="form-floating">
                    <input
                      type="text"
                      className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                      id="userName"
                      placeholder="Enter Username"
                      {...register('userName')}
                    />
                    <label htmlFor="username">Username *</label>
                    {errors.userName && (
                      <div className="invalid-feedback d-block">
                        {errors.userName.message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="mb-4">
                  <div className="form-floating">
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      placeholder="name@example.com"
                      {...register('email')}
                    />
                    <label htmlFor="email">Email Address *</label>
                    {errors.email && (
                      <div className="invalid-feedback d-block">
                        {errors.email.message}
                      </div>
                    )}
                    <div className="form-text">
                      We'll never share your email with anyone else.
                    </div>
                  </div>
                </div>

                {/* Contact Number */}
                <div className="mb-4">
                  <div className="form-floating">
                    <input
                      type="text"
                      className={`form-control ${errors.contactNo ? 'is-invalid' : ''}`}
                      id="contactNo"
                      placeholder="98XXXXXXXX"
                      maxLength="10"
                      {...register('contactNo')}
                      onChange={handleContactChange}
                      onKeyPress={(e) => {
                        // Allow only numbers
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                    <label htmlFor="contactNo">Contact Number *</label>
                    {errors.contactNo && (
                      <div className="invalid-feedback d-block">
                        {errors.contactNo.message}
                      </div>
                    )}
                    <div className="form-text">
                      Enter 10-digit Nepali mobile number (e.g., 9867XXXXXX)
                    </div>
                  </div>
                </div>

                {/* Password */}
                <div className="mb-4">
                  <div className="form-floating position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      placeholder="Password"
                      {...register('password')}
                    />
                    <label htmlFor="password">Password *</label>
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ zIndex: 3 }}
                    >
                      {showPassword ? (
                        <i className="bi bi-eye-slash"></i>
                      ) : (
                        <i className="bi bi-eye"></i>
                      )}
                    </button>
                    {errors.password && (
                      <div className="invalid-feedback d-block">
                        {errors.password.message}
                      </div>
                    )}

                    {/* Password Strength Indicator - FIXED */}
                    <div className="mt-2">
                      <div className="progress" style={{ height: '5px' }}>
                        <div
                          className={`progress-bar bg-${passwordStrength.color}`}
                          role="progressbar"
                          style={{ width: `${progressWidth}%` }}
                          aria-valuenow={progressWidth}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                      <small className={`text-${passwordStrength.color}`}>
                        {passwordStrength.label} {passwordValue && `(${passwordStrength.strength}/5)`}
                      </small>
                    </div>

                    <div className="form-text">
                      Must contain: 8+ characters, uppercase, lowercase, number, special character
                    </div>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="mb-4">
                  <div className="form-floating position-relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      id="confirmPassword"
                      placeholder="Confirm Password"
                      {...register('confirmPassword')}
                    />
                    <label htmlFor="confirmPassword">Confirm Password *</label>
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ zIndex: 3 }}
                    >
                      {showConfirmPassword ? (
                        <i className="bi bi-eye-slash"></i>
                      ) : (
                        <i className="bi bi-eye"></i>
                      )}
                    </button>
                    {errors.confirmPassword && (
                      <div className="invalid-feedback d-block">
                        {errors.confirmPassword.message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div className="mb-4">
                  <div className="form-floating">
                    <textarea
                      className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                      id="address"
                      placeholder="Enter your full address"
                      style={{ height: '100px' }}
                      {...register('address')}
                    ></textarea>
                    <label htmlFor="address">Address *</label>
                    {errors.address && (
                      <div className="invalid-feedback d-block">
                        {errors.address.message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="terms"
                      required
                    />
                    <label className="form-check-label" htmlFor="terms">
                      I agree to the{' '}
                      <a href="#" className="text-primary text-decoration-none">
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-primary text-decoration-none">
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-plus me-2"></i>
                        Create Account
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => reset()}
                    disabled={isSubmitting}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Reset Form
                  </button>
                </div>
              </form>
            </div>

            <div className="card-footer text-center py-3">
              <div className="small">
                Already have an account?{' '}
                <a href="/login" className="text-decoration-none">
                  Sign in here
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;